import { Account, TransportRequest } from '@brenger/api-client';
import { getIdFromIri } from '@brenger/utils';
import { push } from 'connected-react-router';
import { AnyAction, TaskAbortError } from '@reduxjs/toolkit';
import { clarity } from '../../../configs/clarity';
import { r } from '../../../routes';
import { AppListenerApi, startAppListening } from '../../../state/middlewares/listener';
import { logError, logException } from '../../../utils/basics';
import { checkForStringifiedPsGroups } from '../../../utils/checkForStringifiedPsGroups';
import { trackEventPurchase } from '../../../utils/eventTracking';
import { translate } from '../../../utils/localization';
import { coreClient } from '../../../utils/request';
import { getAccountHasBusinessFlow, getLoggedInUser } from '../../User/ducks';
import {
  actions,
  getCustomerDetails,
  getProductPaymentOptIn,
  getQuote,
  getTransportRequestCreationParams,
  validateTransportRequest,
} from '../ducks';
import { GeneralFlowActionTypes, TransportRequestValidationError } from '../typings';

const SES_STORAGE_TR_ATTEMPTS = 'UPLOAD_TR_ATTEMPS';

const isNextPriceEvent = (action: AnyAction): boolean =>
  action.type === GeneralFlowActionTypes.SET_QUOTE || action.type === GeneralFlowActionTypes.RESET_PRICE;

/**
 * A replaced (cancelled) submit stops where it is, without running its error handling (as a cancelled saga did).
 */
function rethrowIfCancelled(e: unknown): void {
  if (e instanceof TaskAbortError) throw e;
}

function monitorUploadAttempts(error: TransportRequestValidationError, api: AppListenerApi): void {
  const attempts = Number(sessionStorage.getItem(SES_STORAGE_TR_ATTEMPTS)) + 1;
  sessionStorage.setItem(SES_STORAGE_TR_ATTEMPTS, String(attempts));
  if (attempts < 2) {
    return;
  }
  // At second attempt let's log it to sentry
  const s = api.getState();
  const state = { tr: s.generalTransport };
  const params = getTransportRequestCreationParams(api.getState());
  logException(`2nd TR upload attempt: FED validation error ${error}`, { state, params });
}

/**
 * Gets a fresh price, validates and creates the transport request, then sends the customer on
 * (same behavior as the former createTr saga).
 * A submit replaced by a later CREATE_TRANSPORT_REQUEST_START stops at its pending price wait or create call.
 */
export async function createTr(api: AppListenerApi): Promise<void> {
  /**
   * Since we live by laws of pricing, first thing we do is getting a fresh quote in.
   */
  // Call for pricing
  api.dispatch(actions.getPrice());
  // Wait till fresh result or something went wrong
  const [nextPriceEvent] = await api.take(isNextPriceEvent);
  // If the price gets reset after getting a new price, something isn't right.
  // It's best to show the user an error, and ask to start over.
  if (nextPriceEvent.type === GeneralFlowActionTypes.RESET_PRICE) {
    monitorUploadAttempts('failed_price_request', api);
    api.dispatch(actions.setTrValidationError('failed_price_request'));
    return;
  }

  /**
   * Validate earlier choices against fresh pricing result
   */
  const validationError = validateTransportRequest(api.getState());
  if (validationError) {
    monitorUploadAttempts(validationError, api);
    api.dispatch(actions.setTrValidationError(validationError));
    return;
  }
  const quote = getQuote(api.getState());
  if (quote?.blocked?.result) {
    // Scroll to top - This is where the blocked reasons are displayed
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  /**
   * Check for malformed Product group data
   */
  // get TR params
  const transportRequestCreationParams = getTransportRequestCreationParams(api.getState());
  const { tr, hasStringifiedPsGroups } = checkForStringifiedPsGroups(transportRequestCreationParams);

  // if there are stringified ps groups, log to Sentry for research
  if (hasStringifiedPsGroups) {
    logException('FLAG stringified items sets in payload (while this should never happen)', { tr });
    // log to console for dev
    logError('FLAG stringified items sets in payload (while this should never happen)');
  }

  // so remove TR attempts from storage
  sessionStorage.removeItem(SES_STORAGE_TR_ATTEMPTS);

  try {
    api.dispatch(actions.toggleSubmitting(true));
    clarity.setTag('transport_request_call');
    const transportRequest = await api.pause(coreClient.transportRequests.create(tr));
    // save uuid and navigate user to payment
    if (transportRequest) {
      clarity.setTag('transport_request_success');
      const uuid = getIdFromIri(transportRequest['@id']);

      /**
       * Introduced for Unified Flow:
       * To keep the purchase events the exact same as before Unified Flow, we fire the purchase event for General or Business flow
       */
      const hasBusinessFlow = getAccountHasBusinessFlow(api.getState());
      const sourceFlow = hasBusinessFlow ? 'Business' : 'General';
      const price = api.getState().generalTransport.quote?.price.incl_vat.amount || 0;
      trackEventPurchase(uuid || null, sourceFlow, (price / 100).toFixed(2));

      await subscribeNewsletter(api);
      /**
       * Where to go next
       * - If core supplies us with a redirect url, we should follow
       * - If product payment, send them to payment
       * - If not guaranteed, send them to the thank you page
       */
      api.dispatch(actions.toggleSubmitting(false));
      if (transportRequest.payment_redirect_url) {
        window.location.assign(transportRequest.payment_redirect_url);
      } else {
        determineNonDirectPaymentAction(transportRequest, api);
      }
    } else {
      api.dispatch(actions.toggleSubmitting(false));
    }
  } catch (err) {
    rethrowIfCancelled(err);
    logError(err);
    clarity.setTag('transport_request_failure');
    api.dispatch(actions.toggleSheet());
    if (err.response?.data?.['hydra:description']) {
      const msg = err.response.data['hydra:description'].replace(/[\r\n]/g, '<br />');
      api.dispatch(actions.toggleSubmitting(false));
      api.dispatch(actions.failedTransportRequest(msg));
      logException(`UPLOAD TR FAILED - Validation: ${err.response.data['hydra:description']}`, {
        error: err.response.data['hydra:description'],
        payload: transportRequestCreationParams,
      });
    } else {
      logException(`UPLOAD TR FAILED - Crash: ${err}`, {
        error: err,
        payload: transportRequestCreationParams,
      });
      const msg = translate('request_flow.errors.handle_request');
      api.dispatch(actions.toggleSubmitting(false));
      api.dispatch(actions.failedTransportRequest(msg));
    }

    openChatCustomerCare();
  }
}

function determineNonDirectPaymentAction(tr: TransportRequest, api: AppListenerApi): void {
  const user = getLoggedInUser(api.getState());
  const uuid = getIdFromIri(tr['@id']);

  const isProductPayment = getProductPaymentOptIn(api.getState());
  if (isProductPayment) {
    api.dispatch(push(r.generalFlow.productPayment.payment({ id: uuid })));
    return;
  }
  const isPayingByInvoice = (user.userData?.account as Account | undefined)?.paying_by_invoice;
  /**
   * So in the first place we really want to determine it ourselfs if the client is allowed to pay by invoice.
   * But! Somehow in very few cases our logged in state is out of sync, frontend state says logged out while core still thinks the user is logged in
   * So, we fall back to the field `needs_payment`, if it is falls we can proceed to the business thank you page.
   */
  if (isPayingByInvoice || !tr.needs_payment) {
    api.dispatch(push(r.payment.thankYou({ id: uuid, type: 'business' })));
    return;
  }
  /**
   * If we end up here we have a problem
   */
  logException('TR UPLOADED without payment action', { tr, user });
}

async function subscribeNewsletter(api: AppListenerApi): Promise<void> {
  const newsletter = api.getState().generalTransport.choices?.newsletter;
  if (!newsletter) {
    return;
  }
  const customerDetails = getCustomerDetails(api.getState());
  try {
    await api.pause(
      coreClient.newsletter.create({
        first_name: customerDetails.first_name as string,
        last_name: customerDetails.last_name as string,
        email: customerDetails.email as string,
      })
    );
  } catch (e) {
    rethrowIfCancelled(e);
    /* Let this fail silent, it is bad luck, but not a show stopper  */
    logError(e);
  }
}

function openChatCustomerCare(): void {
  const body = document.body;
  const timing = body.classList.contains('chat--is-visible') ? 0 : 350;
  body.classList.add('chat--is-visible');
  if (window.fcWidget && typeof window.fcWidget.open !== 'undefined') {
    setTimeout(() => {
      window.fcWidget.open();
    }, timing);
  }
}

/**
 * Registers the GeneralFlow transport request submit (same behavior as the former generalSaga createTr watcher).
 */
export const startCreateTrListeners = (): void => {
  /* takeLatest: a new submit replaces the running one */
  startAppListening({
    type: GeneralFlowActionTypes.CREATE_TRANSPORT_REQUEST_START,
    effect: async (_action, api) => {
      api.cancelActiveListeners();
      await createTr(api);
    },
  });
};
